import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Select, DatePicker, Button, Table, Statistic,
  Segmented, Space, Spin, Typography, Tooltip, message,
} from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function scoreColor(v) {
  if (v == null) return '#8c8c8c';
  if (v >= 90) return '#52c41a';
  if (v >= 75) return '#faad14';
  return '#ff4d4f';
}

function ScorePct({ v }) {
  if (v == null) return <span style={{ color: '#8c8c8c' }}>—</span>;
  return <span style={{ color: scoreColor(v), fontWeight: 500 }}>{v}%</span>;
}

const SECTION_MODES = [
  { label: 'Summary',       value: 'summary' },
  { label: 'By Month',      value: 'month' },
  { label: 'Month Pivot',   value: 'month_pivot' },
  { label: 'FP by Month',   value: 'occ_pivot' },
];

const QUESTION_MODES = [
  { label: 'Summary',     value: 'summary' },
  { label: 'By Month',    value: 'month' },
  { label: 'Month Pivot', value: 'month_pivot' },
];

function buildPivot(data, valueKey, rowKey = (r) => `${r.SectionName}`) {
  const monthKeys = [...new Set(data.map((r) => r.MonthKey))].filter(Boolean).sort();
  const rowMap = {};
  data.forEach((r) => {
    const k = rowKey(r);
    if (!rowMap[k]) rowMap[k] = { SectionName: r.SectionName, QuestionName: r.QuestionName };
    rowMap[k][r.MonthKey] = r[valueKey];
    rowMap[k][`_month_${r.MonthKey}`] = r.Month;
  });
  return { rows: Object.values(rowMap), monthKeys };
}

function PivotTable({ data, valueKey, renderCell, extra = [], buildRowKey }) {
  const { rows, monthKeys } = useMemo(
    () => buildPivot(data, valueKey, buildRowKey),
    [data, valueKey, buildRowKey],
  );
  const monthLabels = useMemo(() => {
    const m = {};
    data.forEach((r) => { if (r.MonthKey) m[r.MonthKey] = r.Month || r.MonthKey; });
    return m;
  }, [data]);

  const columns = [
    { title: 'Section', dataIndex: 'SectionName', key: 'sec', width: 200,
      sorter: (a, b) => (a.SectionName || '').localeCompare(b.SectionName || '') },
    ...extra,
    ...monthKeys.map((mk) => ({
      title: monthLabels[mk] || mk,
      dataIndex: mk,
      key: mk,
      align: 'right',
      width: 100,
      render: (v) => renderCell(v),
    })),
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table
        size="small"
        rowKey={(r, i) => `${r.SectionName}||${r.QuestionName}||${i}`}
        dataSource={rows}
        columns={columns}
        pagination={false}
        bordered
      />
    </div>
  );
}

export default function CXOneScorecardsPage() {
  // CXOne scorecards were entered through 2026-09-30 (evaluations moved into
  // Harbor after), so the page opens on the last quarter of them.
  const CXONE_FROM = dayjs('2026-07-01');
  const CXONE_TO   = dayjs('2026-09-30');

  const [options,    setOptions]    = useState({ campaigns: [], teams: [] });
  const [optLoading, setOptLoading] = useState(true);

  // Filters — campaign is required
  const [dates,    setDates]    = useState([CXONE_FROM, CXONE_TO]);
  const [campaign, setCampaign] = useState(null);

  // Data
  const [sectionData,  setSectionData]  = useState([]);
  const [questionData, setQuestionData] = useState([]);
  const [statsData,    setStatsData]    = useState(null);  // { avg, evals } — always summary
  const [monthlyTrend, setMonthlyTrend] = useState([]);    // [{ month, AvgScore, Count }]
  const [loading,      setLoading]      = useState(false);
  const [searched,     setSearched]     = useState(false);

  // View modes
  const [sectionMode,  setSectionMode]  = useState('summary');
  const [questionMode, setQuestionMode] = useState('summary');

  useEffect(() => {
    api.get('/api/cxone-stats/options')
      .then((r) => {
        setOptions(r.data);
        // Only one visible campaign: picked for them.
        const camps = r.data?.campaigns || [];
        if (camps.length === 1) setCampaign(camps[0].value);
      })
      .catch(() => {})
      .finally(() => setOptLoading(false));
  }, []);

  const filterParams = useCallback(() => ({
    start_date:  dates[0]?.format('YYYY-MM-DD'),
    end_date:    dates[1]?.format('YYYY-MM-DD'),
    ...(campaign ? { campaign_no: campaign } : {}),
  }), [dates, campaign]);

  const fetchSection = useCallback(async (mode) => {
    const r = await api.get('/api/cxone-stats/section-stats', { params: { ...filterParams(), mode } });
    setSectionData(r.data);
  }, [filterParams]);

  const fetchQuestion = useCallback(async (mode) => {
    const r = await api.get('/api/cxone-stats/question-stats', { params: { ...filterParams(), mode } });
    setQuestionData(r.data);
  }, [filterParams]);

  const computeStats = (summaryRows) => {
    if (!summaryRows.length) return { avg: null, evals: 0 };
    const earned = summaryRows.reduce((s, r) => s + (Number(r.TotalEarned) || 0), 0);
    const max    = summaryRows.reduce((s, r) => s + (Number(r.TotalMax)    || 0), 0);
    const avg    = max > 0 ? Math.round((earned / max) * 10000) / 100 : null;
    const evals  = Math.max(...summaryRows.map((r) => r.EvalCount || 0));
    return { avg, evals };
  };

  const handleSearch = useCallback(async () => {
    if (!campaign) {
      message.warning('Please select a Campaign before searching.');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const params = filterParams();
      const [, , summaryRes, monthRes] = await Promise.all([
        fetchSection(sectionMode),
        fetchQuestion(questionMode),
        // Always fetch summary mode for the stat cards
        api.get('/api/cxone-stats/section-stats', { params: { ...params, mode: 'summary' } }),
        // Always fetch month mode for the trend chart
        api.get('/api/cxone-stats/section-stats', { params: { ...params, mode: 'month' } }),
      ]);
      setStatsData(computeStats(summaryRes.data));
      // Aggregate monthly rows into overall avg per month
      const byMonth = {};
      (monthRes.data || []).forEach((r) => {
        const mk = r.MonthKey;
        if (!mk) return;
        if (!byMonth[mk]) byMonth[mk] = { earned: 0, max: 0, count: 0, label: r.Month || mk };
        byMonth[mk].earned += Number(r.TotalEarned) || 0;
        byMonth[mk].max    += Number(r.TotalMax)    || 0;
        byMonth[mk].count   = Math.max(byMonth[mk].count, Number(r.EvalCount) || 0);
      });
      setMonthlyTrend(
        Object.entries(byMonth)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([, d]) => ({
            month:    d.label,
            AvgScore: d.max > 0 ? Math.round((d.earned / d.max) * 1000) / 10 : null,
            Count:    d.count,
          }))
          .filter((r) => r.AvgScore != null)
      );
    } catch {
      // errors surfaced per-table
    } finally {
      setLoading(false);
    }
  }, [fetchSection, fetchQuestion, sectionMode, questionMode, filterParams, campaign]);

  const handleSectionMode = async (mode) => {
    setSectionMode(mode);
    if (!searched) return;
    setSectionData([]);
    try { await fetchSection(mode); } catch {}
  };

  const handleQuestionMode = async (mode) => {
    setQuestionMode(mode);
    if (!searched) return;
    setQuestionData([]);
    try { await fetchQuestion(mode); } catch {}
  };

  const handleExportDetails = async () => {
    if (!campaign) { message.warning('Please select a Campaign before exporting.'); return; }
    setExportLoading(true);
    try {
      const r = await api.get('/api/cxone-stats/question-detail-export', { params: filterParams() });
      const rows = r.data || [];
      const PTS_RE = /\s*-\s*(\d+)\s*pts\s*$/i;
      const cols = [
        { label: 'Agent',     key: 'AgentName' },
        { label: 'Section',   key: '_Sec' },
        { label: 'Question',  key: 'QuestionName' },
        { label: 'Campaign',  key: 'Campaign' },
        { label: 'Month',     key: 'Month' },
        { label: 'Count',     key: 'AnswerCount' },
        { label: 'Score %',   key: 'QuestionScorePct' },
        { label: 'FP',        key: 'FPCount' },
        { label: 'PP',        key: 'PPCount' },
      ];
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return (s.includes(',') || s.includes('"') || s.includes('\n'))
          ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = cols.map((c) => esc(c.label)).join(',');
      const body = rows.map((row) => {
        const r2 = { ...row, _Sec: row.SectionName ? row.SectionName.replace(PTS_RE, '').trim() : row.SectionName };
        return cols.map((c) => esc(r2[c.key])).join(',');
      }).join('\n');
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cxone-question-detail.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed.');
    } finally {
      setExportLoading(false);
    }
  };

  const [exportLoading, setExportLoading] = useState(false);

  const _PTS_RE = /\s*-\s*(\d+)\s*pts\s*$/i;

  // Merge section rows that differ only by the trailing "- XX pts" suffix.
  const sectionDataMerged = useMemo(() => {
    const byMonth = sectionMode !== 'summary';
    const map = new Map();
    const order = [];
    sectionData.forEach((r) => {
      const m = r.SectionName.match(_PTS_RE);
      const baseName = m ? r.SectionName.replace(_PTS_RE, '').trim() : r.SectionName;
      const maxPts   = m ? parseInt(m[1], 10) : 1;
      const k = byMonth ? `${baseName}||${r.MonthKey}` : baseName;
      if (!map.has(k)) {
        map.set(k, { ...r, SectionName: baseName, EvalCount: 0, FPCount: 0, PPCount: 0,
                     TotalEarned: 0, TotalMax: 0, _ws: 0, _wt: 0 });
        order.push(k);
      }
      const acc = map.get(k);
      const pct    = parseFloat(r.SectionScorePct) || 0;
      const weight = (r.EvalCount || 0) * maxPts;
      acc.EvalCount   += r.EvalCount    || 0;
      acc.FPCount     += r.FPCount      || 0;
      acc.PPCount     += r.PPCount      || 0;
      acc.TotalEarned += Number(r.TotalEarned) || 0;
      acc.TotalMax    += Number(r.TotalMax)    || 0;
      acc._ws         += pct * weight;
      acc._wt         += weight;
    });
    return order.map((k) => {
      const row = map.get(k);
      return { ...row, SectionScorePct: row._wt > 0 ? Math.round(row._ws / row._wt * 100) / 100 : null };
    });
  }, [sectionData, sectionMode]); // eslint-disable-line

  // Strip the "- XX pts" suffix from SectionName in question rows (display only, no merging).
  const questionDataDisplay = useMemo(() =>
    questionData.map((r) => ({
      ...r,
      SectionName: r.SectionName ? r.SectionName.replace(_PTS_RE, '').trim() : r.SectionName,
    })),
  [questionData]); // eslint-disable-line

  // --- Section table columns by mode (no Campaign column) ---
  const sectionFlatCols = [
    { title: 'Section', dataIndex: 'SectionName', key: 'sec', width: 200, ellipsis: true,
      sorter: (a, b) => (a.SectionName || '').localeCompare(b.SectionName || '') },
    ...(sectionMode === 'month' ? [{ title: 'Month', dataIndex: 'Month', key: 'mo', width: 110 }] : []),
    { title: 'Evals', dataIndex: 'EvalCount', key: 'ev', width: 80, align: 'right' },
    { title: 'Score', dataIndex: 'SectionScorePct', key: 'sc', width: 90, align: 'right',
      render: (v) => <ScorePct v={v} />,
      sorter: (a, b) => (a.SectionScorePct ?? -1) - (b.SectionScorePct ?? -1) },
    { title: 'FP', dataIndex: 'FPCount', key: 'fp', width: 70, align: 'right' },
    { title: 'PP', dataIndex: 'PPCount', key: 'pp', width: 70, align: 'right' },
  ];

  // --- Question table columns by mode (no Campaign column) ---
  const questionFlatCols = [
    { title: 'Section',  dataIndex: 'SectionName',  key: 'sec', width: 200, ellipsis: true,
      sorter: (a, b) => (a.SectionName || '').localeCompare(b.SectionName || '') },
    { title: 'Question', dataIndex: 'QuestionName', key: 'q', width: 400,
      sorter: (a, b) => (a.QuestionName || '').localeCompare(b.QuestionName || ''),
      render: (v) => {
        if (!v) return '—';
        const short = v.length > 200 ? v.slice(0, 200) + '…' : v;
        return v.length > 200
          ? <Tooltip title={v} overlayStyle={{ maxWidth: 500 }}><span style={{ cursor: 'default' }}>{short}</span></Tooltip>
          : short;
      } },
    ...(questionMode === 'month' ? [{ title: 'Month', dataIndex: 'Month', key: 'mo', width: 110 }] : []),
    { title: 'Count', dataIndex: 'AnswerCount',      key: 'cnt', width: 80, align: 'right' },
    { title: 'Score', dataIndex: 'QuestionScorePct', key: 'sc', width: 90, align: 'right',
      render: (v) => <ScorePct v={v} />,
      sorter: (a, b) => (a.QuestionScorePct ?? -1) - (b.QuestionScorePct ?? -1) },
    { title: 'FP', dataIndex: 'FPCount', key: 'fp', width: 70, align: 'right' },
    { title: 'PP', dataIndex: 'PPCount', key: 'pp', width: 70, align: 'right' },
  ];

  const sectionRowKey  = (r, i) => `${r.SectionName}||${r.Month || ''}||${i}`;
  const questionRowKey = (r, i) => `${r.SectionName}||${r.QuestionName}||${r.Month || ''}||${i}`;

  return (
    <div style={{ padding: '0 4px' }}>
      <Title level={4} style={{ marginBottom: 16 }}>CXOne Scorecard Stats</Title>

      {/* Filter bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Spin spinning={optLoading}>
          <Row gutter={[12, 8]} align="bottom">
            <Col>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Date Range</div>
              <RangePicker
                value={dates}
                onChange={setDates}
                format="YYYY-MM-DD"
                allowClear={false}
                style={{ width: 280 }}
              />
            </Col>
            <Col>
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                Campaign <span style={{ color: '#ff4d4f' }}>*</span>
              </div>
              <Select
                placeholder="Select campaign"
                style={{ width: 280 }}
                value={campaign}
                onChange={setCampaign}
                showSearch
                optionFilterProp="label"
                options={options.campaigns}
                status={!campaign ? 'warning' : undefined}
              />
            </Col>
            <Col>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                Search
              </Button>
            </Col>
          </Row>
        </Spin>
      </Card>

      {/* Summary stat cards — always shown once searched */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Card size="small">
            <Statistic
              title="Total Evals"
              value={statsData != null ? statsData.evals : '—'}
            />
          </Card>
        </Col>
        <Col>
          <Card size="small">
            <Statistic
              title="Avg Score"
              value={statsData?.avg != null ? statsData.avg : '—'}
              suffix={statsData?.avg != null ? '%' : ''}
              valueStyle={{ color: scoreColor(statsData?.avg) }}
            />
          </Card>
        </Col>
      </Row>

      {/* Monthly Trend Chart */}
      {monthlyTrend.length > 0 && (
        <Card size="small" title="Avg Score by Month (Call Date)" style={{ marginBottom: 16 }}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyTrend} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} width={32} />
              <ChartTooltip
                formatter={(v, _, { payload: p }) => [`${v}% (${p.Count} eval${p.Count !== 1 ? 's' : ''})`, 'Avg Score']}
              />
              <Bar dataKey="AvgScore" maxBarSize={60} radius={[4, 4, 0, 0]}>
                {monthlyTrend.map((entry, i) => (
                  <Cell key={i} fill={scoreColor(entry.AvgScore)} />
                ))}
                <LabelList dataKey="AvgScore" position="inside" formatter={(v) => `${v}%`}
                  style={{ fontSize: 11, fontWeight: 600, fill: '#fff' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Section Performance */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="Section Performance"
        extra={
          <Segmented
            size="small"
            options={SECTION_MODES}
            value={sectionMode}
            onChange={handleSectionMode}
          />
        }
      >
        {!searched ? (
          <Text type="secondary">Select a campaign and click Search.</Text>
        ) : loading ? (
          <Spin />
        ) : sectionMode === 'month_pivot' ? (
          <PivotTable
            data={sectionDataMerged}
            valueKey="SectionScorePct"
            renderCell={(v) => <ScorePct v={v} />}
          />
        ) : sectionMode === 'occ_pivot' ? (
          <PivotTable
            data={sectionDataMerged}
            valueKey="FPCount"
            renderCell={(v) => (v != null ? v : '—')}
          />
        ) : (
          <Table
            size="small"
            rowKey={sectionRowKey}
            dataSource={sectionDataMerged}
            columns={sectionFlatCols}
            pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No data' }}
          />
        )}
      </Card>

      {/* Question Performance */}
      <Card
        size="small"
        title="Question Performance"
        extra={
          <Space size="small">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={exportLoading}
              disabled={!searched}
              onClick={handleExportDetails}
            >
              Export Details
            </Button>
            <Segmented
              size="small"
              options={QUESTION_MODES}
              value={questionMode}
              onChange={handleQuestionMode}
            />
          </Space>
        }
      >
        {!searched ? (
          <Text type="secondary">Select a campaign and click Search.</Text>
        ) : loading ? (
          <Spin />
        ) : questionMode === 'month_pivot' ? (
          <PivotTable
            data={questionDataDisplay}
            valueKey="QuestionScorePct"
            renderCell={(v) => <ScorePct v={v} />}
            buildRowKey={(r) => `${r.SectionName}||${r.QuestionName}`}
            extra={[{ title: 'Question', dataIndex: 'QuestionName', key: 'q', width: 200 }]}
          />
        ) : (
          <Table
            size="small"
            rowKey={questionRowKey}
            dataSource={questionDataDisplay}
            columns={questionFlatCols}
            pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No data' }}
          />
        )}
      </Card>
    </div>
  );
}
